import React, { useEffect, useState } from "react";
import { ethers } from "ethers";
import baseWallet from "../../assets/basewallet.png";

// 1) Cookie helper functions
function setCookie(name, value, days) {
  let expires = "";
  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    expires = "; expires=" + date.toUTCString();
  }
  document.cookie = `${name}=${value || ""}${expires}; path=/`;
}

function getCookie(name) {
  const nameEQ = name + "=";
  const ca = document.cookie.split(";");
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i].trim();
    if (c.indexOf(nameEQ) === 0) {
      return c.substring(nameEQ.length, c.length);
    }
  }
  return null;
}

function removeCookie(name) {
  document.cookie = `${name}=; Max-Age=-99999999; path=/`;
}

function formatAddress(addr) {
  if (!addr) return "Connected!";

  // EVM wallets → plain string
  if (typeof addr === "string") {
    return `${addr.slice(0, 7)}...${addr.slice(-5)}`;
  }

  // Phantom-Solana wrapper → has toBase58()
  if (typeof addr.toBase58 === "function") {
    const b58 = addr.toBase58();
    return `${b58.slice(0, 7)}...${b58.slice(-5)}`;
  }

  // Fallback (shouldn't happen)
  return "Connected!";
}

function Login({ chain, updateSigner }) {
  const [providerSet, setProvider] = useState(null);
  const [signerSet, setSigner] = useState(null);
  const [error, setError] = useState(null);
  const [walletType, setWalletType] = useState(null);
  const [showCopiedNotification, setShowCopiedNotification] = useState(false);
  const [currentAccount, setCurrentAccount] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [mevProtectionEnabled, setMevProtectionEnabled] = useState(false);

  // This is the raw EIP-1193 provider we want to listen to for chainChanged
  const [selectedProvider, setSelectedProvider] = useState(null);

  // Control the wallet selection popup
  const [showWalletPopup, setShowWalletPopup] = useState(false);

  // =========================
  // Logout utility function
  // =========================
  function handleLogout() {
    // Clear everything
    setCurrentAccount(null);
    setSigner(null);
    updateSigner(null);
    setProvider(null);
    setWalletType(null);

    // Remove the walletType cookie so it doesn't auto-reconnect
    removeCookie("walletType");
  }

  // =========================
  // Auto–Reconnect on Page Load
  // =========================
  useEffect(() => {
    if (signerSet) return; // If already connected, do nothing

    const storedWallet = getCookie("walletType");
    if (!storedWallet) return; // No cookie => no reconnect

    // Attempt to reconnect based on stored wallet type
    if (storedWallet === "MetaMask") {
      connectMetaMask();
    } else if (storedWallet === "Phantom") {
      connectPhantom();
    } else if (storedWallet === "BNB Wallet") {
      connectBnb();
    }
     else if (storedWallet === "Trust Wallet") {
      connectTrustWallet();
    }
    else if (storedWallet === "Coinbase Wallet") {
      connectCoinbaseWallet()
    }
    else if (storedWallet === "OKX Wallet") {
      connectOKXWallet()
    }
  }, [signerSet]);

  // =========================
  // chainChanged listener
  // =========================
  useEffect(() => {
    if (!selectedProvider) return;

    // Log once so we know we're attaching

    const handleChainChanged = (chainId) => {
      window.location.reload();
    };

    selectedProvider.on("chainChanged", handleChainChanged);

    // Cleanup on unmount or on provider change
    return () => {
      if (typeof selectedProvider.removeListener === "function") {
        selectedProvider.removeListener("chainChanged", handleChainChanged);
      } else if (typeof selectedProvider.off === "function") {
        selectedProvider.off("chainChanged", handleChainChanged);
      }
    };
  }, [selectedProvider]);
 
  // =========================
  // 1. CONNECT METAMASK (Handles nested providers array)
  // =========================
  const connectMetaMask = async () => {
    try {

      if (typeof window === "undefined") {
        setError("No window object available!");
        return;
      }

      let metamaskProvider = null;

      // 1) Check if MetaMask is the default injected provider
      if (
        window.ethereum &&
        
        !window.ethereum.isTrust &&
        !window.ethereum.isTrustWallet &&
        !window.ethereum.isBinance 
      ) {
        metamaskProvider = window.ethereum;
      }

      // 2) Check for multiple providers and handle nested providers
      if (window.ethereum && Array.isArray(window.ethereum.providers)) {
        // First, check top-level providers array
        const possibleMetaMask = window.ethereum.providers.find(
          (p) =>
            p.isMetaMask === true &&
          !Array.isArray(p.providers) &&
            !p.isTrust &&
            !p.isTrustWallet &&
            !p.isBinance // Exclude other wallet types
        );
        if (possibleMetaMask) {
          metamaskProvider = possibleMetaMask;
        } else {
          // If no direct MetaMask found, check nested `.providers`
          for (const provider of window.ethereum.providers) {
            if (provider.providers && Array.isArray(provider.providers)) {
              const nestedMetaMask = provider.providers.find(
                (p) =>
                  !p.isCoinbaseWallet &&
                  !p.isTrust &&
                  !p.isTrustWallet &&
                  !p.isBinance // Exclude other wallet types
              );
              if (nestedMetaMask) {
                metamaskProvider = nestedMetaMask;
                break;
              }
            }
          }
        }
      }
      // If no provider was found, show an error
      if (!metamaskProvider) {
        setError("MetaMask provider not detected!");
        return;
      }

      // 3) Request user accounts
      await metamaskProvider.request({ method: "eth_requestAccounts" });

      // 4) Create an Ethers.js provider & signer
      const provider = new ethers.BrowserProvider(metamaskProvider);
      const signer = await provider.getSigner();

      // 5) Update your app states
      setProvider(provider);
      setSigner(signer);
      updateSigner(signer);
      setWalletType("MetaMask");
      setError(null);

      setCookie("walletType", "MetaMask", 7);

      const accounts = await metamaskProvider.request({
        method: "eth_accounts",
      });
      setCurrentAccount(accounts[0] || null);

      handleSetupAccountChange();
      setShowWalletPopup(false);

      // Set raw EIP-1193 provider
      setSelectedProvider(metamaskProvider);

      console.log("Successfully connected to MetaMask.");
    } catch (e) {
      console.error(e);
      setError("An error occurred while connecting to MetaMask.");
    }
  };

  // =========================
  // 2. CONNECT PHANTOM (EVM)
  // =========================
  const connectPhantom = async () => {
    try {
      if (typeof window === "undefined") {
        setError("No window object available!");
        return;
      }

      let phantomProvider = null;

      // 1) Check if 'phantom' in window
      if ("phantom" in window) {
        const anyWindow = window;
        phantomProvider = anyWindow?.phantom?.ethereum;

        if (phantomProvider) {
          await phantomProvider.request({ method: "eth_requestAccounts" });
          const provider = new ethers.BrowserProvider(phantomProvider);
          const signer = await provider.getSigner();

          setProvider(provider);
          setSigner(signer);
          updateSigner(signer);
          setWalletType("Phantom");
          setError(null);

          setCookie("walletType", "Phantom", 7);

          const accounts = await phantomProvider.request({
            method: "eth_accounts",
          });
          setCurrentAccount(accounts[0] || null);

          handleSetupAccountChange();
          setShowWalletPopup(false);

          // raw EIP-1193 provider
          setSelectedProvider(phantomProvider);
          return;
        }
      }

      // 2) If not found, check window.ethereum.providers array
      if (window.ethereum && Array.isArray(window.ethereum.providers)) {
        const possiblePhantom = window.ethereum.providers.find(
          (p) => p.isPhantom || p.isPhantomEVM
        );
        if (possiblePhantom) {
          phantomProvider = possiblePhantom;
          await phantomProvider.request({ method: "eth_requestAccounts" });

          const provider = new ethers.BrowserProvider(phantomProvider);
          const signer = await provider.getSigner();

          setProvider(provider);
          setSigner(signer);
          updateSigner(signer);
          setWalletType("Phantom");
          setError(null);

          setCookie("walletType", "Phantom", 7);

          const accounts = await phantomProvider.request({
            method: "eth_accounts",
          });
          setCurrentAccount(accounts[0] || null);

          handleSetupAccountChange();
          setShowWalletPopup(false);

          setSelectedProvider(phantomProvider);
          return;
        } else {
          console.warn("Phantom not found in providers array...");
        }
      }

      // 3) Final fallback: single window.ethereum
      if (window.ethereum && window.ethereum.isPhantom) {
        await window.ethereum.request({ method: "eth_requestAccounts" });

        const provider = new ethers.BrowserProvider(window.ethereum);
        const signer = await provider.getSigner();

        setProvider(provider);
        setSigner(signer);
        updateSigner(signer);
        setWalletType("Phantom");
        setError(null);

        setCookie("walletType", "Phantom", 7);

        const accounts = await window.ethereum.request({
          method: "eth_accounts",
        });
        setCurrentAccount(accounts[0] || null);

        handleSetupAccountChange();
        setShowWalletPopup(false);

        setSelectedProvider(window.ethereum);
        return;
      }

      setError("Phantom Wallet (EVM) not detected!");
    } catch (e) {
      console.error(e);
      setError("An error occurred while connecting to Phantom.");
    }
  };

  // =========================
  // 3. CONNECT BNB
  // =========================
  const connectBnb = async () => {
    try {
      let binanceProvider = null;
  
      // 1) Check if user is in the new "Binance dApp browser"
      if (window.ethereum && window.ethereum.isBinance) {
        binanceProvider = window.ethereum;
      }
      // 2) Otherwise, check for the older "BinanceChain" injection
      else if (window.BinanceChain) {
        binanceProvider = window.BinanceChain;
      }
  
      // If we still don't have a provider, user doesn't have a Binance wallet
      if (!binanceProvider) {
        setError("Binance Chain Wallet not detected!");
        return;
      }
  
      // 3) Request user accounts
      await binanceProvider.request({ method: "eth_requestAccounts" });
  
      // 4) Create an Ethers.js provider & signer
      const provider = new ethers.BrowserProvider(binanceProvider);
      const signer = await provider.getSigner();
  
      // 5) Update your app states
      setProvider(provider);
      setSigner(signer);
      updateSigner(signer);
      setError(null);
      setWalletType("BNB Wallet");
  
      setCookie("walletType", "BNB Wallet", 7);
  
      const accounts = await binanceProvider.request({
        method: "eth_accounts",
      });
      setCurrentAccount(accounts?.[0] ?? null);
  
      handleSetupAccountChange();
      setShowWalletPopup(false);
  
      // raw EIP-1193 provider
      setSelectedProvider(binanceProvider);
    } catch (e) {
      console.error(e);
      setError("An error occurred while connecting to BNB Wallet.");
    }
  };

  // -------------------------
  // Helpers for Trust Wallet
  // -------------------------
  function getTrustWalletFromWindow() {
    const isTrustWallet = (ethereum) =>
      !!ethereum?.isTrust || !!ethereum?.isTrustWallet;

    if (typeof window === "undefined" || !window.ethereum) {
      return null;
    }

    // 1) Check if window.ethereum itself is Trust
    if (isTrustWallet(window.ethereum)) {
      return window.ethereum;
    }

    // 2) If there's a providers array
    if (window.ethereum.providers) {
      const trust = window.ethereum.providers.find(isTrustWallet);
      if (trust) return trust;
    }

    // 3) Some older versions might expose global
    if (typeof window.trustwallet !== "undefined") {
      return window.trustwallet;
    }

    return null;
  }

  function listenForTrustWalletInitialized({ timeout = 3000 } = {}) {
    return new Promise((resolve) => {
      const handleInitialization = () => {
        resolve(getTrustWalletFromWindow());
      };

      window.addEventListener("trustwallet#initialized", handleInitialization, {
        once: true,
      });

      setTimeout(() => {
        window.removeEventListener(
          "trustwallet#initialized",
          handleInitialization,
          { once: true }
        );
        resolve(null);
      }, timeout);
    });
  }

  async function getTrustWalletInjectedProvider({ timeout = 3000 } = {}) {
    const provider = getTrustWalletFromWindow();
    if (provider) {
      return provider;
    }
    return listenForTrustWalletInitialized({ timeout });
  }

  // =========================
  // 4. CONNECT TRUST WALLET (EVM)
  // =========================
  const connectTrustWallet = async () => {
    try {
      if (typeof window === "undefined") {
        setError("No window object available!");
        return;
      }

      const trustProvider = await getTrustWalletInjectedProvider({ timeout: 3000 });
      if (!trustProvider) {
        setError("Trust Wallet not detected!");
        return;
      }

      await trustProvider.request({ method: "eth_requestAccounts" });
      const provider = new ethers.BrowserProvider(trustProvider);
      const signer = await provider.getSigner();

      setProvider(provider);
      setSigner(signer);
      updateSigner(signer);
      setWalletType("Trust Wallet");
      setError(null);

      setCookie("walletType", "Trust Wallet", 7);

      const accounts = await trustProvider.request({
        method: "eth_accounts",
      });
      setCurrentAccount(accounts?.[0] || null);

      handleSetupAccountChange();
      setShowWalletPopup(false);

      // raw EIP-1193 provider
      setSelectedProvider(trustProvider);
    } catch (err) {
      console.error(err);
      setError(
        "Something went wrong. Please try setting Trust Wallet as default if issue persists."
      );
    }
  };

  // =========================
  // 5. CONNECT COINBASE WALLET
  // =========================
  const connectCoinbaseWallet = async () => {
    try {
      console.log("Connecting to Coinbase Wallet...");

      if (typeof window === "undefined") {
        setError("No window object available!");
        return;
      }

      let coinbaseProvider = null;


      // 1) Check if Coinbase Wallet is the default injected provider
      if (window.ethereum && window.ethereum.isCoinbaseWallet) {
        coinbaseProvider = window.ethereum;
      }

      // 2) Check for multiple providers and handle nested providers
      if (window.ethereum && Array.isArray(window.ethereum.providers)) {
        // First, check top-level providers array
        const possibleCoinbase = window.ethereum.providers.find(
          (p) => p.isCoinbaseWallet
        );
        if (possibleCoinbase) {
          coinbaseProvider = possibleCoinbase;
        } else {
          // If no direct Coinbase Wallet found, check nested `.providers`
          for (const provider of window.ethereum.providers) {
            if (provider.providers && Array.isArray(provider.providers)) {
              const nestedCoinbase = provider.providers.find(
                (p) => p.isCoinbaseWallet
              );
              if (nestedCoinbase) {
                coinbaseProvider = nestedCoinbase;
                break;
              }
            }
          }
        }
      }


      // If no provider was found, show an error
      if (!coinbaseProvider) {
        setError("Coinbase Wallet not detected!");
        return;
      }

      // 3) Request user accounts
      await coinbaseProvider.request({ method: "eth_requestAccounts" });

      // 4) Create an Ethers.js provider & signer
      const provider = new ethers.BrowserProvider(coinbaseProvider);
      const signer = await provider.getSigner();

      // 5) Update your app states
      setProvider(provider);
      setSigner(signer);
      updateSigner(signer);
      setWalletType("Coinbase Wallet");
      setError(null);

      setCookie("walletType", "Coinbase Wallet", 7);

      const accounts = await coinbaseProvider.request({
        method: "eth_accounts",
      });
      setCurrentAccount(accounts?.[0] || null);

      handleSetupAccountChange();
      setShowWalletPopup(false);

      // Set raw EIP-1193 provider
      setSelectedProvider(coinbaseProvider);
    } catch (e) {
      console.error(e);
      setError("An error occurred while connecting to Coinbase Wallet.");
    }
  };

  // =========================
  // 5. CONNECT OKX WALLET
  // =========================
  const connectOKXWallet = async () => {
    try {
      // Ensure window.ethereum exists and is an OKX Wallet\
      //for (const key in window.ethereum) {
      //  console.log(key, await window.ethereum[key]);
      //}
          
      if (!window.ethereum ) {
        setError("OKX Wallet not detected!");
        return;
      }
      const windowCopy = await Object.getOwnPropertyDescriptors(window.ethereum)

      let okxProvider = window.ethereum.providers?.find((p) => p.isOKXWallet);

      if (okxProvider) {
      } 
      else {
        if(windowCopy.isOkxWallet?.value && windowCopy.isOkxWallet.value === true){
          okxProvider = window.ethereum
        }
        else{
          setError("OKX Wallet not detected!");
          return;
        }
      }
      // Request user accounts
      await okxProvider.request({ method: "eth_requestAccounts" });
      // Create an Ethers.js provider and signer
      const provider = new ethers.BrowserProvider(okxProvider);
      const signer = await provider.getSigner();

      // Update your app states
      setProvider(provider);
      setSigner(signer);
      updateSigner(signer);
      setError(null);
      setWalletType("OKX Wallet");

      // Set a cookie to remember the wallet type
      setCookie("walletType", "OKX Wallet", 7);

      // Fetch and set the current account
      const accounts = await okxProvider.request({ method: "eth_accounts" });
      setCurrentAccount(accounts?.[0] ?? null);

      // Handle additional setup tasks
      handleSetupAccountChange();
      setShowWalletPopup(false);

      // Set the raw EIP-1193 provider
      setSelectedProvider(okxProvider);
    } catch (e) {
      console.error(e);
      setError("An error occurred while connecting to OKX Wallet.");
    }
  };

  /* ------------------------------------------------------------- */
  /*  Clean wrapper:     address + sign power + getAddress()       */
  /* ------------------------------------------------------------- */
  class NeonSignerViaPhantom {
    constructor(solUser, phantom, provider, proxy) {
      this.solUser = solUser;   
      this.phantom = phantom;
      this._provider = provider;
      this._proxy = proxy
    }

    //new ethers.JsonRpcProvider(NEON_PROXY_RPC_URL)

    /** 0x… address to use in Neon RPC calls (ethers-style property) */
    //solana address
    get address() {
      return this.solUser.publicKey;
    }

    /** ethers-style method so code can call signer.getAddress() */
    getAddress() {
      return this.solUser.neonWallet;
    }

    get provider() {
      return this._provider
    }

    get network() {
      return this._provider.getNetwork()
    }

    get proxy() {
      return this._proxy
    }

    //signer.getAddress()
    //signer.network
    //signer.provider.getNetwork()

    /*
    get solanaPubKey() {
      return this.solUser.publicKey.toBase58();
    }
    */

    /** Sign & send a Solana tx via Phantom UI */
    async signAndSend(tx, conn) { //CHECK
      const { signature } = await this.phantom.signAndSendTransaction(tx);
      return conn.confirmTransaction(signature, "confirmed");
    }
  }


  // Solana/Neon support removed for ETH-only

  // =========================
  // Utility: Account change listeners
  // =========================
  const handleSetupAccountChange = () => {
    // For EVM wallets
    if (window.ethereum) {
      window.ethereum.on("accountsChanged", handleAccountsChanged);
    }
    // For BNB
    if (window.BinanceChain) {
      window.BinanceChain.on("accountsChanged", handleAccountsChanged);
    }
  };

  const handleAccountsChanged = async (accounts) => {
    if (!accounts || accounts.length === 0) {
      setCurrentAccount(null);
      setSigner(null);
      updateSigner(null);
      setProvider(null);
      setWalletType(null);
      removeCookie("walletType");
      return;
    }

    const newAccount = accounts[0];
    setCurrentAccount(newAccount);

    // Re-init the signer
    try {
      if (walletType === "MetaMask" && window.ethereum?.isMetaMask) {
        const provider = new ethers.BrowserProvider(window.ethereum);
        const signer = await provider.getSigner();
        setProvider(provider);
        setSigner(signer);
        updateSigner(signer);
      } else if (walletType === "Phantom" && window.ethereum?.isPhantom) {
        const provider = new ethers.BrowserProvider(window.ethereum);
        const signer = await provider.getSigner();
        setProvider(provider);
        setSigner(signer);
        updateSigner(signer);
      } else if (walletType === "BNB Wallet" && window.BinanceChain) {
        const provider = new ethers.BrowserProvider(window.BinanceChain);
        const signer = await provider.getSigner();
        setProvider(provider);
        setSigner(signer);
        updateSigner(signer);
      } else if (
        walletType === "Trust Wallet" &&
        (window.ethereum?.isTrust || window.ethereum?.isTrustWallet)
      ) {
        const provider = new ethers.BrowserProvider(window.ethereum);
        const signer = await provider.getSigner();
        setProvider(provider);
        setSigner(signer);
        updateSigner(signer);
      }
    } catch (err) {
      console.error("Error resetting provider/signers:", err);
    }
  };

  // =========================
  // CUSTOM RPC (MEV) - MetaMask only
  // =========================
  const addCustomRPC = async () => {
    if (!window.ethereum || walletType !== "MetaMask") {
      alert(
        "This functionality is only supported on MetaMask. Please add the RPC URL manually."
      );
      return;
    }
    try {
      await window.ethereum.request({
        method: "wallet_addEthereumChain",
        params: [
          {
            chainId: "0x1", // Ethereum Mainnet
            chainName: "Ethereum Flashbots",
            rpcUrls: ["https://rpc.flashbots.net/fast?originId=ethervista"],
            nativeCurrency: {
              name: "Ether",
              symbol: "ETH",
              decimals: 18,
            },
            blockExplorerUrls: ["https://etherscan.io"],
          },
        ],
      });
      setMevProtectionEnabled(true);
    } catch (error) {
      console.error("Error adding custom RPC:", error);
      alert("Failed to add the RPC. Please try again or add it manually.");
    }
  };

  // =========================
  // RENDER
  // =========================
  return (
    <div className="flex gap-2 relative text-white">
      {signerSet && walletType ? (
        // If EVM-based wallet is connected
        <div className="flex">
          <div className="z-50 text-bold bg-white/10 hover:bg-white/15 py-[5px] px-2 sm:px-3 text-[11px] sm:text-[13px] rounded-full font-[500] outfit flex items-center border border-transparent hover:border-white/40 transition-all duration-300">
            {/* Wallet Icons */}
            {walletType === "MetaMask" && (
              <img
                src="https://upload.wikimedia.org/wikipedia/commons/thumb/3/36/MetaMask_Fox.svg/2048px-MetaMask_Fox.svg.png"
                alt="MetaMask"
                className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 mr-2 sm:mr-3"
              />
            )}
            {walletType === "BNB Wallet" && (
              <img
                src="https://icons.veryicon.com/png/o/miscellaneous/bitcoin-currency/bnb.png"
                alt="BNB Wallet"
                className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 mr-2 sm:mr-3"
              />
            )}
            {walletType === "Phantom" && (
              <img
                src="/phantom.png"
                alt="Phantom"
                className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 mr-2 sm:mr-3 py-1 px-[3px] rounded-lg"
              />
            )}
            
            {walletType === "Trust Wallet" && (
              <img
                src="https://avatars.githubusercontent.com/u/32179889?s=200&v=4"
                alt="Trust Wallet"
                className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 mr-2 sm:mr-3"
              />
            )}
            {walletType === "Coinbase Wallet" && (
              <img
                src={baseWallet}
                alt="Coinbase Wallet"
                className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 mr-2 sm:mr-3 rounded-full"
              />
            )}
            {walletType === "OKX Wallet" && (
              <img
              src="https://altcoinsbox.com/wp-content/uploads/2023/03/okx-logo.jpg"
              alt="OKX Wallet"
              className="w-4 h-4 sm:w-5 sm:h-5 md:w-5 md:h-5 mr-2 sm:mr-3 rounded-full"
            />
            )}

            {/* Show address (shortened) */}
            
            {formatAddress(signerSet?.address)}

            {/* Logout Button */}
            {signerSet && (
              <button
                onClick={handleLogout}
                className="text-gray-400 hover:text-white ml-2 md:ml-3 opacity-80 hover:opacity-100"
                title="Logout"
              >
                <img className="w-4 h-4 sm:w-5 sm:h-5 py-[3px]" src="/logout2.svg" />
              </button>
            )}
          </div>

          {/* Settings (MEV Protection) for MetaMask */}
          {chain === "ethereum" && walletType === "MetaMask" && (
            <button
              className="bg-white/10 hover:text-yellow-300/80 hover:bg-yellow-400/10  border border-transparent hover:border-yellow-200/40 transition-all duration-300 rounded-full text-bold  ml-1 text-[13px] lg:text-[16px] px-1sm:ml-2 px-2 xs:px-2 sm:px-3"
              onClick={() => setSettingsOpen(true)}
            >
              &#x1F916;
            </button>
          )}

          {/* MEV Popup */}
          {settingsOpen && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
              onClick={() => setSettingsOpen(false)}
            >
              <div
                className="bg-[#12151e] rounded-[17px] p-6 max-w-md w-full relative"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  className="absolute top-5 right-7 text-gray-300 hover:text-gray-100"
                  onClick={() => setSettingsOpen(false)}
                >
                  ✕
                </button>
                <h2 className="text-lg font-[400] mb-5  text-white/90 kanit-light ">
                  BOT Protection
                </h2>

                <div className="flex items-center gap-2 mb-1">
                  <label className="text-white/90 font-medium kanit-light mb-1 ">
                    MEV Protection
                  </label>
                </div>

                <p className="text-sm text-start font-[300] text-white/40  kanit-light mb-5">
                  MEV Protection shields you from front-running attacks on
                  Ethereum by configuring a custom RPC URL in Metamask. Once you
                  add the Flashbot RPC to Metamask and activate it, your
                  transactions are secured with anti-bot protection.
                </p>

                <div className="flex justify-end gap-4 text-sm">
                  <button
                    className="px-6 py-2 bg-white/5 border border-white/10 text-white/80 hover:bg-white/10 rounded-md font-semibold"
                    onClick={() => setSettingsOpen(false)}
                  >
                    Close
                  </button>
                  {mevProtectionEnabled ? (
                    <button
                      disabled
                      className="px-5 py-2 border flex gap-2 bg-white/5  border-white/10 text-white/80 hover:bg-white/10 rounded-md font-semibold"
                    >
                      Added{" "}
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="w-5 h-5 hidden sm:block text-green-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </button>
                  ) : (
                    <button
                      className="px-6 py-2 border bg-white/5 border-white/10  hover:bg-white/10 rounded-md font-semibold text-white/80"
                      onClick={addCustomRPC}
                    >
                      Add
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        // If not connected, show "Connect" button
        <>
          <button
  className="z-50 
    text-white  bg-gradient-to-r from-[#faffbfb5] via-[#c3c17e] to-[#faffbfb5]
 hover:brightness-110
    border border-transparent 
    shadow-[0_0_10px_rgba(255,200,150,0.4)] 
    transition-all duration-300 
    py-[5px] text-[11px] px-3 
    xxs:text-[10px] xxs:px-4  
    xs:text-[12px] sm:text-[14px] sm:px-7 
    rounded-full font-[500] outfit 
    flex items-center"
  onClick={() => setShowWalletPopup(true)}
>
  Connect
</button>

          {showWalletPopup && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
              onClick={() => {
                setShowWalletPopup(false);
                setError(null);
              }}
            >
              <div
                className="bg-[#12151e] rounded-xl px-5 pt-2 pb-2 max-w-[330px] w-full relative "
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  className="absolute top-4 right-6 text-gray-300 hover:text-gray-100"
                  onClick={() => {
                    setShowWalletPopup(false);
                    setError(null);
                  }}
                >
                  ✕
                </button>

                <div className="flex flex-col gap-1 pt-3 mt-6">
                  <button
                    onClick={connectMetaMask}
                    className="flex items-center gap-4 py-3 px-4 bg-white/5 hover:bg-white/10 rounded-md"
                  >
                    <img
                      src="https://upload.wikimedia.org/wikipedia/commons/thumb/3/36/MetaMask_Fox.svg/2048px-MetaMask_Fox.svg.png"
                      alt="MetaMask"
                      className="w-8 h-8"
                    />
                    <span className="text-white text-sm font-[500]">
                      MetaMask
                    </span>
                  </button>
                  
                  <button
                    onClick={connectPhantom}
                    className="flex items-center gap-4 py-3 px-4 bg-white/5 hover:bg-white/10 rounded-md"
                  >
                    <img
                      src="https://187760183-files.gitbook.io/~/files/v0/b/gitbook-x-prod.appspot.com/o/spaces%2F-MVOiF6Zqit57q_hxJYp%2Ficon%2FU7kNZ4ygz4QW1rUwOuTT%2FWhite%20Ghost_docs_nu.svg?alt=media&token=447b91f6-db6d-4791-902d-35d75c19c3d1"
                      alt="Phantom"
                      className="w-8 h-8 rounded-[4px]"
                    />
                    <span className="text-white text-sm font-[500]">
                      Phantom
                    </span>
                  </button>
                  <button
                    onClick={connectBnb}
                    className="flex items-center gap-4 py-3 px-4 bg-white/5 hover:bg-white/10 rounded-md"
                  >
                    <img
                      src="https://icons.veryicon.com/png/o/miscellaneous/bitcoin-currency/bnb.png"
                      alt="BNB Wallet"
                      className="w-8 h-8"
                    />
                  <span className="text-white text-sm text-start font-[500]">
                    BNB (or Binance wallet&nbsp; 
                    <a 
                      href="https://www.binance.com/en/web3wallet" 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="text-blue-400 hover:underline hover:text-blue-300"
                    >
                       In-app
                    </a>
                    )
                  </span>
                  </button>
                  <button
                    onClick={connectTrustWallet}
                    className="flex items-center gap-4 py-3 px-4 bg-white/5 hover:bg-white/10 rounded-md"
                  >
                    <img
                      src="https://avatars.githubusercontent.com/u/32179889?s=200&v=4"
                      alt="Trust Wallet"
                      className="w-8 h-8 rounded-md"
                    />
                    <span className="text-white text-sm font-[500]">
                      Trust Wallet
                    </span>
                  </button>
                  <button
                    onClick={connectCoinbaseWallet}
                    className="flex items-center gap-4 py-3 px-4 bg-white/5 hover:bg-white/10 rounded-md"
                  >
                    <img
                      src={baseWallet}
                      alt="Coinbase Wallet"
                      className="w-8 h-8 rounded-md"
                    />
                    <span className="text-white text-sm font-[500]">
                      Coinbase Wallet
                    </span>
                  </button>
                  <button
                    onClick={connectOKXWallet}
                    className="flex items-center gap-4 py-3 px-4 bg-white/5 hover:bg-white/10 rounded-md"
                  >
                    <img
                      src="https://altcoinsbox.com/wp-content/uploads/2023/03/okx-logo.jpg"
                      alt="OKX Wallet"
                      className="w-8 h-8 rounded-md"
                    />
                    <span className="text-white text-sm font-[500]">
                      OKX Wallet
                    </span>
                  </button>
                  {error ? (
                    <p className="text-yellow-500 text-sm  font-[600] py-2">
                      {error}
                    </p>
                  ) : (
                    <div className="h-[34px]" />
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default Login;
